// RD-4141: one place that decides what a flight card says about the flight's state, so the header
// badge, the card badge, the colours and the labels can never disagree with each other.
//
// Pure (no React, no I/O) so every state can be exercised with a hand-written payload.

export const FLIGHT_CARD_STATUS = {
  SCHEDULED: 'scheduled',
  ON_TIME: 'onTime',
  DEPARTED: 'departed',
  DELAYED: 'delayed',
  LANDED: 'landed',
  CANCELLED: 'cancelled',
  DIVERTED: 'diverted',
  RETURNED: 'returned',
}

// Badge text and colour per state. Green = nothing wrong, red = the passenger should look.
const BADGE = {
  [FLIGHT_CARD_STATUS.SCHEDULED]: { label: 'SCHEDULED', tone: 'green' },
  [FLIGHT_CARD_STATUS.ON_TIME]: { label: 'ON TIME', tone: 'green' },
  [FLIGHT_CARD_STATUS.DEPARTED]: { label: 'DEPARTED', tone: 'green' },
  [FLIGHT_CARD_STATUS.DELAYED]: { label: 'DELAYED', tone: 'red' },
  [FLIGHT_CARD_STATUS.LANDED]: { label: 'LANDED', tone: 'green' },
  [FLIGHT_CARD_STATUS.CANCELLED]: { label: 'CANCELLED', tone: 'red' },
  [FLIGHT_CARD_STATUS.DIVERTED]: { label: 'DIVERTED', tone: 'red' },
  [FLIGHT_CARD_STATUS.RETURNED]: { label: 'RETURNED', tone: 'red' },
}

const clean = value =>
  String(value == null ? '' : value)
    .trim()
    .toLowerCase()

const upper = value =>
  String(value == null ? '' : value)
    .trim()
    .toUpperCase()

// The provider's status string (flightapi.io displayStatus: Scheduled, Delayed, Departed, In Air,
// Landed, Arrived, Canceled; the card also tolerates On Time, Diverted/Redirected, Returned) plus
// what the lookup computed about delays, into one of the eight states.
//
// Departed vs On time: the provider separates "Departed" (just left) from "In Air" (en route).
// An airborne flight that is not late reads "Departed" or "On time" accordingly.
export const getFlightCardStatus = ({
  flightInfo,
  delayDepartureTime,
  delayArrivalTime,
  progressPercentage,
} = {}) => {
  const raw = clean(flightInfo && flightInfo.flight_status)
  const departureIata = upper(
    flightInfo && flightInfo.departure && flightInfo.departure.iata
  )
  const arrivalIata = upper(
    flightInfo && flightInfo.arrival && flightInfo.arrival.iata
  )

  if (/cancel/.test(raw)) return FLIGHT_CARD_STATUS.CANCELLED
  if (/return/.test(raw)) return FLIGHT_CARD_STATUS.RETURNED
  if (/divert|redirect/.test(raw)) {
    // A diversion that ends where it started is a return.
    return departureIata && departureIata === arrivalIata
      ? FLIGHT_CARD_STATUS.RETURNED
      : FLIGHT_CARD_STATUS.DIVERTED
  }
  if (raw === 'arrived' || raw === 'landed' || progressPercentage >= 100) {
    return FLIGHT_CARD_STATUS.LANDED
  }
  if (raw === 'delayed' || delayDepartureTime || delayArrivalTime) {
    return FLIGHT_CARD_STATUS.DELAYED
  }
  if (raw === 'departed') return FLIGHT_CARD_STATUS.DEPARTED
  if (
    raw === 'in air' ||
    raw === 'en route' ||
    raw === 'en-route' ||
    raw === 'on time' ||
    raw === 'ontime'
  ) {
    return FLIGHT_CARD_STATUS.ON_TIME
  }
  // Scheduled, and any string we do not know: neutral, never "on time".
  return FLIGHT_CARD_STATUS.SCHEDULED
}

export const flightCardBadge = status =>
  BADGE[status] || BADGE[FLIGHT_CARD_STATUS.SCHEDULED]

const isAirborne = status =>
  [
    FLIGHT_CARD_STATUS.DEPARTED,
    FLIGHT_CARD_STATUS.ON_TIME,
    FLIGHT_CARD_STATUS.DELAYED,
    FLIGHT_CARD_STATUS.DIVERTED,
    FLIGHT_CARD_STATUS.RETURNED,
  ].includes(status)

// 0-100: where the plane sits on the route line.
export const flightCardProgress = (status, progressPercentage) => {
  if (status === FLIGHT_CARD_STATUS.LANDED) return 100
  if (
    status === FLIGHT_CARD_STATUS.SCHEDULED ||
    status === FLIGHT_CARD_STATUS.CANCELLED
  )
    return 0
  const value = Number(progressPercentage)
  const known = Number.isFinite(value) && value > 1
  if (isAirborne(status)) {
    // Diverted / returned flights are mid-air by definition; a delayed flight that has not left
    // yet sits at the start.
    if (known) return Math.min(Math.max(value, 4), 96)
    return status === FLIGHT_CARD_STATUS.DIVERTED ||
      status === FLIGHT_CARD_STATUS.RETURNED
      ? 50
      : 0
  }
  return 0
}

// What each side of the card says. `departed` is whether the flight has left the gate.
export const flightCardLabels = (status, progress) => {
  const departed = progress > 1 || status === FLIGHT_CARD_STATUS.LANDED
  const departure =
    status === FLIGHT_CARD_STATUS.SCHEDULED ||
    status === FLIGHT_CARD_STATUS.CANCELLED
      ? 'SCHEDULED DEPARTURE'
      : departed || status !== FLIGHT_CARD_STATUS.DELAYED
      ? 'DEPARTED'
      : 'ESTIMATED DEPARTURE'
  const arrival =
    status === FLIGHT_CARD_STATUS.LANDED
      ? 'ARRIVED'
      : status === FLIGHT_CARD_STATUS.SCHEDULED ||
        status === FLIGHT_CARD_STATUS.CANCELLED
      ? 'SCHEDULED ARRIVAL'
      : 'ESTIMATED ARRIVAL'
  return { departure, arrival }
}

// The colour of a time value: green when fine, red when late or cancelled (Figma: a cancelled
// flight's scheduled times are red), plain when the flight went somewhere else (its time no
// longer says "on schedule" or "late").
export const flightCardTimeTone = status => {
  if (
    status === FLIGHT_CARD_STATUS.DELAYED ||
    status === FLIGHT_CARD_STATUS.CANCELLED
  ) {
    return 'red'
  }
  if (
    status === FLIGHT_CARD_STATUS.DIVERTED ||
    status === FLIGHT_CARD_STATUS.RETURNED
  ) {
    return 'plain'
  }
  return 'green'
}

// The line under the route names for a flight that did not go where it was booked to. Needs the
// airport the flight was expected at (the booking's), so it is omitted when that is unknown or
// when the flight did land there.
export const flightCardNote = (status, { expectedIata } = {}) => {
  const expected = upper(expectedIata)
  if (!expected) return null
  if (status === FLIGHT_CARD_STATUS.DIVERTED) {
    return `Originally to ${expected} · Diverted in flight`
  }
  if (status === FLIGHT_CARD_STATUS.RETURNED) {
    return `Originally to ${expected} · Returned in flight`
  }
  return null
}

// The arrival a new booking is made for, from a lookup's `time`: the provider's estimate when the
// flight is late (the card's "ESTIMATED ARRIVAL"), else the schedule. Ops: a booking created while
// the card shows an estimate keeps the estimated time. `arrivalEstimate` is only set for flights
// within a day and stays on the scheduled day, so the booked date never moves.
export const bookedArrivalTime = time => {
  const scheduled = (time && time.arrival) || null
  const estimate = time && time.arrivalEstimate
  if (!scheduled || !estimate) return scheduled
  return Date.parse(estimate) > Date.parse(scheduled) ? estimate : scheduled
}
